import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MousePointerClick, Globe, MapPin, Search, ExternalLink, TrendingUp, ChevronDown, Megaphone } from 'lucide-react';
import { resolvePaidChannels, channelMeta, describeChannels, labelHref } from '../../services/paidChannels';
import AdCreativeViewer from '../../services/AdCreativeViewer';

const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Truncated, not rounded: 1,589 reads as 1.5K. Rounding would show 1.6K, i.e. a
// number the affiliate never had. Divided as integers because 2900/1000*10 is
// 28.999999999999996 in floating point, which would floor to 2.8K.
const formatNumber = (num) => {
  const n = Number(num) || 0;
  if (!n) return '0';
  if (n >= 1000000) return (Math.floor(n / 100000) / 10).toFixed(1) + 'M';
  if (n >= 1000) return (Math.floor(n / 100) / 10).toFixed(1) + 'K';
  return n.toLocaleString();
};

const withScheme = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

/**
 * Paid Traffic Finder — affiliates found running ads, on Google sponsored
 * results or in Meta's Ad Library.
 *
 * The API returns one row per recorded find, but the same affiliate is often
 * spotted several times, so rows are grouped by affiliate: one card each,
 * expandable to show every find. A find is a keyword for Google and an
 * advertising Page for Meta, which is why the expanded list is headed "found N
 * times" rather than counting keywords.
 *
 * Counts and timestamps are deliberately not rendered — users see which
 * affiliates are advertising and where, not how many we logged or how recently.
 *
 * Shows the three newest, like Newly Added Affiliates. Older ones queue behind
 * them and surface as those are cleared. Nothing expires on a timer: an
 * affiliate stays until an admin takes it off paid traffic.
 *
 * The box renders nothing at all when the feed is empty, so a quiet day leaves
 * the dashboard exactly as it was.
 */
const VISIBLE_COUNT = 3;
const PaidTrafficFinder = () => {
  const [entries, setEntries] = useState([]);
  const [expandedUrl, setExpandedUrl] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/feed`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success) setEntries(data.data || []);
    } catch {
      /* dashboard widget — stay silent and keep whatever is on screen */
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  // One card per affiliate. The feed arrives newest-first, so the first row seen
  // for an affiliate is its most recent find.
  const affiliates = useMemo(() => {
    const map = new Map();

    for (const entry of entries) {
      if (!map.has(entry.affiliate_url)) {
        map.set(entry.affiliate_url, {
          affiliate_url: entry.affiliate_url,
          vertical: entry.vertical,
          organic_traffic: entry.organic_traffic,
          // The live column, not the find-time snapshot — otherwise editing an
          // affiliate's paid traffic leaves the dashboard showing the old figure.
          paid_traffic: entry.adwords_traffic ?? entry.new_traffic,
          // Likewise the affiliate's current channels, not this entry's, so a
          // channel removed since the find stops being advertised here.
          channels: resolvePaidChannels(entry.paid_channels, entry.adwords_traffic ?? entry.new_traffic),
          top_page: entry.top_page,
          finds: []
        });
      }

      const group = map.get(entry.affiliate_url);
      // A Meta find carries no keyword — the advertising Page is what was seen.
      const label = entry.advertiser_name || entry.keyword;
      if (!label && !entry.country) continue;

      const alreadyListed = group.finds.some(
        (f) =>
          (f.label || '').toLowerCase() === (label || '').toLowerCase() &&
          (f.country || '') === (entry.country || '') &&
          f.channel === entry.channel
      );
      if (!alreadyListed) {
        group.finds.push({
          id: entry.id,
          label,
          channel: entry.channel,
          platforms: entry.platforms,
          ad_images: entry.ad_images,
          country: entry.country,
          traffic: entry.new_traffic
        });
      }
    }

    // Newest three; the rest wait their turn behind them.
    return Array.from(map.values()).slice(0, VISIBLE_COUNT);
  }, [entries]);

  if (affiliates.length === 0) return null;

  const toggle = (url) => setExpandedUrl((current) => (current === url ? null : url));

  return (
    <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-4 mb-5 transition-colors duration-300">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            {/* Clicks, not spend — deliberately not a currency icon. */}
            <MousePointerClick className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full animate-ping" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300">Paid Traffic Finder</h3>
            <p className="text-[11px] text-gray-400 dark:text-slate-500">
              Affiliates spotted on Google sponsored ads and social
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 dark:bg-amber-900/30 rounded-full">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Live</span>
        </div>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {affiliates.map((affiliate) => {
          const isExpanded = expandedUrl === affiliate.affiliate_url;
          const primary = affiliate.finds[0];
          const extraCount = Math.max(0, affiliate.finds.length - 1);
          const topPages = (affiliate.top_page || '')
            .split(',')
            .map((p) => p.trim())
            .filter(Boolean);

          return (
            <div key={affiliate.affiliate_url}>
              <div
                onClick={() => toggle(affiliate.affiliate_url)}
                className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors cursor-pointer
                  ${
                    isExpanded
                      ? 'bg-amber-50/60 dark:bg-slate-800/60 border-amber-200 dark:border-amber-800/40'
                      : 'border-gray-100 dark:border-slate-700 hover:bg-amber-50/60 dark:hover:bg-slate-800'
                  }`}
              >
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Globe className="w-4 h-4 text-white" />
                </div>

                <div className="flex-1 min-w-0">
                  <a
                    href={withScheme(affiliate.affiliate_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 font-medium text-sm text-gray-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:underline truncate"
                    title={affiliate.affiliate_url}
                  >
                    <span className="truncate">{affiliate.affiliate_url}</span>
                    <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-60" />
                  </a>

                  {primary && (
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-0.5">
                      {primary.label && (
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] ${
                            primary.channel === 'meta'
                              ? 'text-indigo-600 dark:text-indigo-400'
                              : 'text-blue-600 dark:text-blue-400'
                          }`}
                        >
                          {/* A megaphone for an advertiser, a magnifier for a
                              search keyword — the two are not the same kind of
                              thing and should not look alike. */}
                          {primary.channel === 'meta' ? (
                            <Megaphone className="w-2.5 h-2.5 flex-shrink-0" />
                          ) : (
                            <Search className="w-2.5 h-2.5 flex-shrink-0" />
                          )}
                          {primary.label}
                        </span>
                      )}
                      {primary.country && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-slate-400">
                          <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
                          {primary.country}
                        </span>
                      )}
                      {extraCount > 0 && (
                        <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400">
                          +{extraCount} more
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Where the ads are running. The single most useful thing to
                      scan for on this box, so it sits next to the figure. */}
                  {affiliate.channels.length > 0 && (
                    <span
                      className="inline-flex items-center gap-0.5"
                      title={`Advertising on ${describeChannels(affiliate.channels)}`}
                    >
                      {affiliate.channels.map(channelMeta).filter(Boolean).map((channel) => (
                        <span
                          key={channel.key}
                          className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold ${channel.solid}`}
                        >
                          {channel.letter}
                        </span>
                      ))}
                    </span>
                  )}
                  {affiliate.vertical && (
                    <span className="hidden sm:inline px-2 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                      {affiliate.vertical.split(',')[0]}
                    </span>
                  )}
                  <div className="text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <TrendingUp className="w-3 h-3 text-amber-500" />
                      <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                        {formatNumber(affiliate.paid_traffic)}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wide">paid</p>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 dark:text-slate-500 transition-transform duration-200 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </div>

              {isExpanded && (
                <div className="mt-1 p-4 space-y-4 rounded-lg bg-gradient-to-b from-gray-50 to-white dark:from-slate-800/50 dark:to-slate-900 border border-gray-100 dark:border-slate-700 animate-expandIn">
                  {/* Every find recorded against this affiliate */}
                  <div>
                    <h4 className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">
                      Found {affiliate.finds.length} {affiliate.finds.length === 1 ? 'time' : 'times'}
                    </h4>
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                      {affiliate.finds.length > 0 ? (
                        <div className="divide-y divide-gray-50 dark:divide-slate-700">
                          {affiliate.finds.map((find, i) => {
                            const findChannel = channelMeta(find.channel);
                            return (
                              <div key={find.id} className="flex items-center gap-2 px-3 py-2.5">
                                <span
                                  title={findChannel?.where}
                                  className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                                    findChannel ? findChannel.solid : 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
                                  }`}
                                >
                                  {findChannel ? findChannel.letter : i + 1}
                                </span>
                                <span className="flex-1 min-w-0 text-xs text-gray-700 dark:text-slate-300 truncate">
                                  {/* A pasted landing page is a link; a Page
                                      name like "Football Duke" is not. */}
                                  {labelHref(find.label) ? (
                                    <a
                                      href={labelHref(find.label)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-blue-600 hover:underline dark:text-blue-400"
                                      title={find.label}
                                    >
                                      {find.label}
                                    </a>
                                  ) : (
                                    find.label || '—'
                                  )}
                                  {find.platforms && (
                                    <span className="ml-1.5 text-[11px] text-gray-400 dark:text-slate-500">
                                      {find.platforms}
                                    </span>
                                  )}
                                  {/* The creative captured when the find was logged. */}
                                  <span className="ml-1.5">
                                    <AdCreativeViewer images={find.ad_images} label={find.label} compact />
                                  </span>
                                </span>
                                {find.country && (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-slate-400 flex-shrink-0">
                                    <MapPin className="w-2.5 h-2.5" />
                                    {find.country}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="px-3 py-4 text-center text-xs text-gray-400 dark:text-slate-500">
                          Nothing recorded
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Domain stats */}
                  <div>
                    <h4 className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">
                      Domain Stats
                    </h4>
                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                      <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-slate-700">
                        <div className="p-3 text-center">
                          <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                            Organic Traffic
                          </p>
                          <p className="text-base font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                            {affiliate.organic_traffic ? formatNumber(affiliate.organic_traffic) : '—'}
                          </p>
                        </div>
                        <div className="p-3 text-center">
                          <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                            Paid Traffic
                          </p>
                          <p className="text-base font-bold text-amber-600 dark:text-amber-400 mt-1">
                            {formatNumber(affiliate.paid_traffic)}
                          </p>
                        </div>
                        <div className="p-3 text-center">
                          <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                            Vertical
                          </p>
                          <p className="text-base font-bold text-gray-800 dark:text-white mt-1 truncate">
                            {affiliate.vertical ? affiliate.vertical.split(',')[0] : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Top pages */}
                  {topPages.length > 0 && (
                    <div>
                      <h4 className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">
                        Top Pages
                      </h4>
                      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                        <div className="divide-y divide-gray-50 dark:divide-slate-700 max-h-40 overflow-y-auto">
                          {topPages.slice(0, 5).map((page, i) => (
                            <a
                              key={i}
                              href={withScheme(page)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors group"
                            >
                              <span className="w-5 h-5 rounded-md bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-[10px] font-medium text-gray-500 dark:text-slate-400 flex-shrink-0">
                                {i + 1}
                              </span>
                              <span className="flex-1 min-w-0 text-xs text-gray-600 dark:text-slate-400 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                {page.replace(/^https?:\/\/(www\.)?/, '')}
                              </span>
                              <ExternalLink className="w-3 h-3 text-gray-300 dark:text-slate-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <a
                    href={withScheme(affiliate.affiliate_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Visit {affiliate.affiliate_url}
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes expandIn {
          0% { opacity: 0; transform: translateY(-4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-expandIn { animation: expandIn 0.2s ease-out; }
      `}</style>
    </div>
  );
};

export default PaidTrafficFinder;
